import type { SocketId } from "@excalidraw/excalidraw/types";

type IncomingEvents = {
  "client-broadcast": [ArrayBuffer, Uint8Array<ArrayBuffer>];
  "connect_error": [Error];
  "first-in-room": [];
  "init-room": [];
  "new-user": [string];
  "room-user-change": [SocketId[]];
  "stags:reconnect": [];
  "user-follow-room-change": [SocketId[]];
};

type Listener<TArgs extends unknown[]> = (
  ...args: TArgs
) => void | Promise<void>;

type StoredListener = {
  callback: (...args: never[]) => unknown;
  once: boolean;
};

type WireEnvelope = {
  args: unknown[];
  event: string;
};

type BinaryMarker = {
  __stagsBinary: "ArrayBuffer" | "Uint8Array";
  data: string;
};

const MAX_QUEUED_MESSAGES = 64;
const RECONNECT_MAX_DELAY_MS = 15_000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const bytesToBase64 = (bytes: Uint8Array) => {
  const chunkSize = 0x8000;
  let binary = "";
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return window.btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const encodeValue = (value: unknown): unknown => {
  if (value instanceof ArrayBuffer) {
    return {
      __stagsBinary: "ArrayBuffer",
      data: bytesToBase64(new Uint8Array(value)),
    } satisfies BinaryMarker;
  }
  if (value instanceof Uint8Array) {
    return {
      __stagsBinary: "Uint8Array",
      data: bytesToBase64(value),
    } satisfies BinaryMarker;
  }
  if (Array.isArray(value)) {
    return value.map(encodeValue);
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, encodeValue(item)]),
    );
  }
  return value;
};

const decodeValue = (value: unknown): unknown => {
  if (
    isRecord(value) &&
    (value.__stagsBinary === "ArrayBuffer" ||
      value.__stagsBinary === "Uint8Array") &&
    typeof value.data === "string"
  ) {
    const bytes = base64ToBytes(value.data);
    return value.__stagsBinary === "ArrayBuffer" ? bytes.buffer : bytes;
  }
  if (Array.isArray(value)) {
    return value.map(decodeValue);
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, decodeValue(item)]),
    );
  }
  return value;
};

const websocketRoomUrl = (baseUrl: string, roomId: string) => {
  const url = new URL(baseUrl);
  if (url.protocol === "https:") {
    url.protocol = "wss:";
  } else if (url.protocol === "http:") {
    url.protocol = "ws:";
  } else if (url.protocol !== "wss:" && url.protocol !== "ws:") {
    throw new Error("Stags collaboration URL must use HTTP(S) or WS(S)");
  }
  url.pathname = `${url.pathname.replace(/\/$/, "")}/room/${encodeURIComponent(
    roomId,
  )}`;
  url.search = "";
  url.hash = "";
  return url.toString();
};

export class StagsCollabSocket {
  id?: string;

  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempt = 0;
  private hasOpened = false;
  private closedByClient = false;
  private connectionErrorDispatched = false;
  private readonly listeners = new Map<string, Set<StoredListener>>();
  private readonly queuedIncoming = new Map<string, unknown[][]>();
  private readonly queuedOutgoing: string[] = [];

  constructor(private readonly url: string) {
    this.connect();
  }

  on<K extends keyof IncomingEvents>(
    event: K,
    callback: Listener<IncomingEvents[K]>,
  ) {
    this.addListener(event, callback, false);
    return this;
  }

  once<K extends keyof IncomingEvents>(
    event: K,
    callback: Listener<IncomingEvents[K]>,
  ) {
    this.addListener(event, callback, true);
    return this;
  }

  off<K extends keyof IncomingEvents>(
    event: K,
    callback?: Listener<IncomingEvents[K]>,
  ) {
    if (!callback) {
      this.listeners.delete(event);
      return this;
    }
    const entries = this.listeners.get(event);
    if (!entries) {
      return this;
    }
    for (const entry of entries) {
      if (entry.callback === callback) {
        entries.delete(entry);
      }
    }
    if (entries.size === 0) {
      this.listeners.delete(event);
    }
    return this;
  }

  emit(event: string, ...args: unknown[]) {
    const payload = JSON.stringify({
      event,
      args: args.map(encodeValue),
    } satisfies WireEnvelope);
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(payload);
      if (event === "join-room") {
        this.flushQueuedOutgoing();
      }
    } else if (event !== "server-volatile-broadcast") {
      if (this.queuedOutgoing.length >= MAX_QUEUED_MESSAGES) {
        this.queuedOutgoing.shift();
      }
      this.queuedOutgoing.push(payload);
    }
    return this;
  }

  close() {
    this.closedByClient = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close(1000, "client_close");
    this.socket = null;
  }

  private addListener<K extends keyof IncomingEvents>(
    event: K,
    callback: Listener<IncomingEvents[K]>,
    once: boolean,
  ) {
    const entries = this.listeners.get(event) || new Set<StoredListener>();
    entries.add({
      callback: callback as StoredListener["callback"],
      once,
    });
    this.listeners.set(event, entries);
    this.flushQueuedIncoming(event);
  }

  private connect() {
    if (this.closedByClient) {
      return;
    }

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket) {
        return;
      }
      this.reconnectAttempt = 0;
      this.connectionErrorDispatched = false;
      if (this.hasOpened) {
        this.dispatch("stags:reconnect", []);
      }
      this.hasOpened = true;
    });

    socket.addEventListener("message", (message) => {
      if (this.socket !== socket || typeof message.data !== "string") {
        return;
      }
      try {
        const parsed: unknown = JSON.parse(message.data);
        if (
          !isRecord(parsed) ||
          typeof parsed.event !== "string" ||
          !Array.isArray(parsed.args)
        ) {
          throw new Error("Invalid collaboration message");
        }
        const args = parsed.args.map(decodeValue);
        if (parsed.event === "stags:connected") {
          if (typeof args[0] !== "string") {
            throw new Error("Missing collaboration connection id");
          }
          this.id = args[0];
          return;
        }
        this.dispatch(parsed.event, args);
      } catch (error) {
        this.dispatchConnectionError(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    });

    socket.addEventListener("error", () => {
      this.dispatchConnectionError(new Error("Collaboration connection failed"));
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      this.id = undefined;
      if (!this.closedByClient) {
        this.scheduleReconnect();
      }
    });
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null || this.closedByClient) {
      return;
    }
    const delay = Math.min(
      1000 * 2 ** this.reconnectAttempt,
      RECONNECT_MAX_DELAY_MS,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private flushQueuedOutgoing() {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }
    for (const payload of this.queuedOutgoing.splice(0)) {
      this.socket.send(payload);
    }
  }

  private dispatchConnectionError(error: Error) {
    if (this.connectionErrorDispatched) {
      return;
    }
    this.connectionErrorDispatched = true;
    this.dispatch("connect_error", [error]);
  }

  private dispatch(event: string, args: unknown[]) {
    const entries = this.listeners.get(event);
    if (!entries?.size) {
      const queued = this.queuedIncoming.get(event) || [];
      if (queued.length >= MAX_QUEUED_MESSAGES) {
        queued.shift();
      }
      queued.push(args);
      this.queuedIncoming.set(event, queued);
      return;
    }

    for (const entry of [...entries]) {
      if (entry.once) {
        entries.delete(entry);
      }
      try {
        Promise.resolve(entry.callback(...(args as never[]))).catch((error) => {
          console.error("Stags collaboration listener failed", error);
        });
      } catch (error) {
        console.error("Stags collaboration listener failed", error);
      }
    }
    if (entries.size === 0) {
      this.listeners.delete(event);
    }
  }

  private flushQueuedIncoming(event: string) {
    const queued = this.queuedIncoming.get(event);
    if (!queued?.length) {
      return;
    }
    this.queuedIncoming.delete(event);
    for (const args of queued) {
      this.dispatch(event, args);
    }
  }
}

export const createStagsCollabSocket = (baseUrl: string, roomId: string) =>
  new StagsCollabSocket(websocketRoomUrl(baseUrl, roomId));
